import React, { createContext, useContext, useState, useEffect } from 'react';
import { Brand } from '../types/photos';
import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';

interface AuthContextType {
  brand: Brand | null;
  isAuthenticated: boolean;
  setBrand: (brand: Brand | null) => void;
  session: Session | null;
}

const AuthContext = createContext<AuthContextType>({
  brand: null,
  isAuthenticated: false,
  setBrand: () => { },
  session: null
});

const getBrandData = async (userId: string) => {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching brand data:', error);
    return null;
  }

  return data;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [brand, setBrand] = useState<Brand | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        // Set initial brand data from session
        setBrand({
          id: session.user.id,
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Unknown',
          email: session.user.email || '',
          password: '',
          storeCredit: 0,
          createdAt: new Date().toISOString()
        });

        // Fetch full brand data from database
        getBrandData(session.user.id).then(brandData => {
          if (brandData) {
            setBrand({
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Unknown',
              email: session.user.email || '',
              password: '',
              storeCredit: 0,
              createdAt: new Date().toISOString(),
              ...brandData
            });
          }
        });
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        // Set initial brand data from session
        setBrand({
          id: newSession.user.id,
          name: newSession.user.user_metadata?.name || newSession.user.email?.split('@')[0] || 'Unknown',
          email: newSession.user.email || '',
          password: '',
          storeCredit: 0,
          createdAt: new Date().toISOString()
        });

        // Fetch full brand data from database
        getBrandData(newSession.user.id).then(brandData => {
          if (brandData) {
            setBrand({
              id: newSession.user.id,
              name: newSession.user.user_metadata?.name || newSession.user.email?.split('@')[0] || 'Unknown',
              email: newSession.user.email || '',
              password: '',
              storeCredit: 0,
              createdAt: new Date().toISOString(),
              ...brandData
            });
          }
        });
      } else {
        setBrand(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    brand,
    isAuthenticated: !!session,
    setBrand,
    session
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
